<template>
  <c-modal
    v-model:open="open"
    :title="$viewport.isMd ? title : ''"
    size="900"
    spacing="0"
    @close="$emit('close')"
  >
    <div
      v-if="eifuStore.detailViewFile"
      :class="b(modifiers)"
    >
      <!-- Note: it was not possible to switch 'data' attribute depending on screen size. -->
      <!-- Note: In case the 'data' file does not exist, macOS Safari may crash... -->
      <object
        v-if="$viewport.isMd"
        :key="eifuStore.detailViewFile.fileUrl"
        :class="b('preview')"
        :data="eifuStore.detailViewFile.fileUrl"
        type="application/pdf"
      >
        <div :class="b('preview-fallback')">
          <img
            :class="b('preview-image')"
            :src="eifuStore.detailViewFile.previewImage"
            alt=""
            width="960"
            height="1280"
          />
        </div>
      </object>

      <component
        :is="$viewport.isMd ? 'e-scrollable' : 'div'"
        :class="b('sidebar')"
      >
        <a
          :class="b('read-instructions')"
          role="button"
          @click="onClickReadInstructions"
        >
          <e-icon-sprite
            v-if="isTheme('default')"
            icon="i-info"
            size="35"
          />
          {{ matchThemeTranslation(themeConfig.translations.readInstructions) }}
        </a>
        <component :is="$viewport.isMd ? 'aside' : 'div'">
          <c-collapse-group>
            <c-collapse
              :title="matchThemeTranslation(themeConfig.translations.currentVersion)"
              :active="eifuStore.detailViewFile === currentVersionFile"
              padding="0"
              size="300"
              keep-in-view
              @after-open="onAfterOpen(currentVersionFile)"
            >
              <img
                v-if="!$viewport.isMd && eifuStore.detailViewFile.previewImage"
                :class="b('preview-image')"
                :src="eifuStore.detailViewFile.previewImage"
                alt=""
                width="960"
                height="1280"
              />
              <c-electronic-instruction-tile
                :class="b('active-file')"
                :item="currentVersionFile"
                width="narrow"
                content="preview"
                actions="preview"
                border="none"
              />
            </c-collapse>

            <template v-if="fileHistory.length">
              <h4 :class="b('history-title')">
                {{ matchThemeTranslation(themeConfig.translations.historyTitle) }}
              </h4>

              <ul :class="b('list')">
                <li
                  v-if="eifuStore.relationParentFile"
                  :class="b('list-item')"
                >
                  <c-collapse
                    :title="collapseTitle(eifuStore.relatedAssetFile)"
                    :active="eifuStore.detailViewFile === eifuStore.relatedAssetFile"
                    padding="0"
                    size="300"
                    keep-in-view
                    @after-open="onAfterOpen(eifuStore.relatedAssetFile)"
                  >
                    <img
                      v-if="!$viewport.isMd && eifuStore.detailViewFile.previewImage"
                      :class="b('preview-image')"
                      :src="eifuStore.detailViewFile.previewImage"
                      alt=""
                      width="960"
                      height="1280"
                    />
                    <c-electronic-instruction-tile
                      :item="eifuStore.relatedAssetFile"
                      width="narrow"
                      content="preview"
                      actions="preview-history"
                      border="none"
                      :show-subscription-on-download="false"
                    />
                  </c-collapse>
                </li>
                <li
                  v-for="file in fileHistory"
                  :key="file.fileId"
                  :class="b('list-item')"
                >
                  <c-collapse
                    :title="collapseTitle(file)"
                    :active="eifuStore.detailViewFile === file"
                    padding="0"
                    size="300"
                    keep-in-view
                    @after-open="onAfterOpen(file)"
                  >
                    <img
                      v-if="!$viewport.isMd && eifuStore.detailViewFile.previewImage"
                      :class="b('preview-image')"
                      :src="eifuStore.detailViewFile.previewImage"
                      alt=""
                      width="960"
                      height="1280"
                    />
                    <c-electronic-instruction-tile
                      :item="file"
                      width="narrow"
                      content="preview"
                      actions="preview-history"
                      border="none"
                      :show-subscription-on-download="false"
                    />
                  </c-collapse>
                </li>
              </ul>
            </template>
          </c-collapse-group>
        </component>
      </component>
    </div>
  </c-modal>
</template>

<script>
  import useEifuStore from '@/stores/eifu';
  import cCollapseGroup from '@/components/c-collapse-group.vue';
  import cCollapse from '@/components/c-collapse.vue';
  import cElectronicInstructionTile from '@/components/c-electronic-instruction-tile.vue';
  import cModal from '@/components/c-modal.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import eScrollable from '@/elements/e-scrollable.vue';
  import { formatDate } from '@/helpers/format-date-time.js';
  import themes from '@/mixins/themes.js';

  /**
   * Renders an electronic instruction detail modal.
   */
  export default {
    name: 'c-electronic-instruction-detail',

    components: {
      cCollapse,
      cCollapseGroup,
      eIconSprite,
      eScrollable,
      cElectronicInstructionTile,
      cModal,
    },
    mixins: [themes],

    // props: {},

    emits: ['close', 'clickReadInstructions'],
    setup() {
      return {
        eifuStore: useEifuStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Object} Holds theme specific options for the component.
         */
        themeConfig: {
          translations: {
            readInstructions: {
              default: 'c-electronic-instruction-detail.readInstructions',
              sagemax: 'c-sagemax-electronic-instruction-detail.readInstructions',
            },
            currentVersion: {
              default: 'c-electronic-instruction-detail.currentVersion',
              sagemax: 'c-sagemax-electronic-instruction-detail.currentVersion',
            },
            historyTitle: {
              default: 'c-electronic-instruction-detail.historyTitle',
              sagemax: 'c-sagemax-electronic-instruction-detail.historyTitle',
            },
            titleValidAsOf: {
              default: 'c-electronic-instruction-detail.titleValidAsOf',
              sagemax: 'c-sagemax-electronic-instruction-detail.titleValidAsOf',
            },
            validAsOf: {
              default: 'c-electronic-instruction-detail.validAsOf',
              sagemax: 'c-sagemax-electronic-instruction-detail.validAsOf',
            },
          },
        },
      };
    },

    computed: {
      /**
       * Returns a definition of BEM modifiers.
       *
       * @returns {Object}
       */
      modifiers() {
        const { theme } = this;

        return {
          theme,
        };
      },

      /**
       * Gets the current version for the provided detailViewFile.
       *
       * @returns {Object|null}
       */
      currentVersionFile() {
        if (!this.eifuStore.detailViewFile) {
          return null;
        }

        return (
          this.eifuStore.relationParentFile ||
          this.eifuStore.entries.find((file) => file.id === this.eifuStore.detailViewFile?.id) ||
          this.eifuStore.getFlatRelations.find((file) => file.id === this.eifuStore.detailViewFile?.id)
        );
      },

      /**
       * Returns an array with the complete history for the current file.
       * Only items that have a `validAsOf`-date are included.
       *
       * @returns {Array.<Object>}
       */
      fileHistory() {
        const { currentVersionFile } = this;

        return currentVersionFile
          ? this.eifuStore.history[currentVersionFile.id]?.filter((item) => !!item.validAsOf) || []
          : [];
      },

      /**
       * Handles the open state of the modal.
       */
      open: {
        get() {
          return !!this.eifuStore.detailViewFile;
        },
        set(open) {
          if (!open) {
            this.eifuStore.detailViewFile = null;
            this.eifuStore.showAllDetailValues = false;
          }
        },
      },

      /**
       * Returns a dynamic title based on the shown detail.
       *
       * @returns {String}
       */
      title() {
        const { brandNames, validAsOf } = this.eifuStore.detailViewFile || {};

        return [
          Array.isArray(brandNames) ? brandNames.join(', ') : '',
          validAsOf &&
            this.matchThemeTranslation(this.themeConfig.translations.titleValidAsOf, {
              date: formatDate(validAsOf),
            }),
        ]
          .filter(Boolean)
          .join(', ');
      },
    },
    watch: {
      /**
       * Watches the currentVersionFile computed to load the related history, if required.
       *
       * @param {Object} file - The current file related to the current instance.
       * @param {Object} oldFile - The previous value.
       */
      currentVersionFile: {
        immediate: true,
        handler(file, oldFile) {
          const { id } = file || {};

          if (id && (!oldFile || id !== oldFile?.id) && !this.eifuStore.history[id]) {
            this.eifuStore.apiFetchHistory(id);
          }
        },
        deep: true,
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Returns a title string for a collapsable.
       *
       * @param {Object} file - A file definition object.
       *
       * @returns {String}
       */
      collapseTitle(file) {
        return this.matchThemeTranslation(this.themeConfig.translations.validAsOf, {
          date: formatDate(file.validAsOf),
        });
      },

      /**
       * Handles the afterOpen event of c-collapsable components.
       *
       * @param {Object} file - A file definition object.
       */
      onAfterOpen(file) {
        this.eifuStore.detailViewFile = file;
      },

      /**
       * Handler for click on read instructions button.
       */
      onClickReadInstructions() {
        this.$emit('clickReadInstructions');
        this.open = false;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  // @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-electronic-instruction-detail {
    $this: &;
    $grid-area--preview: preview;
    $grid-area--sidebar: sidebar;

    display: grid;
    grid-template-areas:
      '#{$grid-area--preview}'
      '#{$grid-area--sidebar}';
    min-height: 50vh;

    @include mixins.media(md) {
      grid-template-areas: '#{$grid-area--preview} #{$grid-area--sidebar}';
      grid-template-columns: 1fr functions.rem(400px);
      grid-template-rows: 85vh;
      padding-right: functions.rem(variables.$spacing--10); // Scrollbar spacing
    }

    &--theme-sagemax {
      #{$this}__read-instructions {
        color: functions.get-theme-var('color-secondary-3');
        font-size: functions.calc-rem(variables.$font-size--16);
        font-weight: variables.$font-weight--regular;
      }

      .c-collapse__toggle {
        padding: 0 !important; // stylelint-disable-line declaration-no-important
      }

      .c-electronic-instruction-tile__inner {
        padding-right: 0;
        padding-left: 0;
      }

      .c-electronic-instruction-tile__actions {
        padding: 0 functions.rem(variables.$spacing--40);
      }
    }

    &__preview {
      grid-area: $grid-area--preview;
      width: 100%;
      height: 100%;
    }

    &__preview-fallback {
      height: 100%;
      background: variables.$color-grayscale--100;
    }

    &__preview-image {
      object-fit: contain;
      width: 100%;
      height: auto;
      max-height: 100%;
      padding: functions.rem(variables.$spacing--10);
      background: variables.$color-grayscale--200;

      @include mixins.media(sm) {
        padding: functions.rem(variables.$spacing--20);
      }

      @include mixins.media(md) {
        padding: functions.rem(variables.$spacing--40);
      }
    }

    &__sidebar {
      grid-area: $grid-area--sidebar;
      padding: functions.rem(0 variables.$spacing--30 variables.$spacing--30);

      @include mixins.media(md) {
        padding-top: functions.rem(variables.$spacing--30);
      }

      .simplebar-content {
        padding-top: 0 !important; // stylelint-disable-line declaration-no-important -- required because of inline styles
      }
    }

    &__history-title {
      margin: functions.rem(variables.$spacing--50 0 variables.$spacing--10);
      font-weight: variables.$font-weight--light;
    }

    &__list {
      @extend %list-reset;

      .c-electronic-instruction-tile {
        margin-bottom: functions.rem(variables.$spacing--30);
      }
    }

    &__read-instructions {
      @extend %link--primary-1;
      @include mixins.font(variables.$font-size--14, null, variables.$font-weight--semi-bold);

      display: flex;
      align-items: center;
      padding: functions.rem(variables.$spacing--5 0);
      border-bottom: 1px solid functions.get-theme-var('c-collapse-separator-color--expanded');
      cursor: pointer;
      font-weight: variables.$font-weight--semi-bold;

      .e-icon-sprite {
        width: functions.rem(35px);
        height: functions.rem(35px);
      }
    }

    .c-electronic-instruction-tile {
      &--has-indicator {
        @include mixins.media($down: sm) {
          padding-top: functions.rem(variables.$spacing--25);
        }
      }
    }

    &__preview-image ~ .c-electronic-instruction-tile .c-electronic-instruction-tile__status-indicator {
      @include mixins.media($down: sm) {
        top: auto;
        bottom: 100%;
      }
    }

    .c-collapse__toggle {
      @include mixins.media(md) {
        padding: functions.rem(0 variables.$spacing--20);
      }
    }
  }
</style>
